import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { MapLayerData } from "../services/types";
import { capitalizeWords, formatValueBase, parseMarkdown } from "../utils/helpers";
import LoadingIndicator from "../components/LoadingIndicator";
import { layersEnv } from "../utils/environment";

const MapItemDetails = () => {
    const [layerData, setLayerData] = useState<MapLayerData | null>(null);
    const [loading, setLoading] = useState<boolean>(true);

    const [searchParams] = useSearchParams();
    
    // Set layerId, point id, and partnerId from search params    
    const layerId = searchParams.get('layerId');
    const pointId = searchParams.get('id');

    // Initialize layer details and summary info
    const details = layerData?.details;
    // const summary = layerData?.summary[0];

    // Define current point layer item based on search params
    const items = layerData?.items;
    const item = items?.find(item => Number(item.id) === Number(pointId));

    console.log('item:', item);

    // Format Activity Date
    const date = item?.activity_date ? new Date(item.activity_date) : null;

    const formattedDate = date
        ? `${String(date.getUTCMonth() + 1).padStart(2, '0')}/${String(date.getUTCDate()).padStart(2, '0')}/${date.getUTCFullYear()}`
        : '';


    // Fetch layer details, items, and summary when layerId changes
    useEffect(() => {
        const fetchLayerData = async () => {
            if (!layerId) return;
            setLoading(true);

            try {
                const response = await fetch(
                `${import.meta.env.VITE_DASHBOARD_BACKEND_URL}/api/getlayeritems?layerId=${layerId}&environment=${layersEnv}`,
                {
                    method: 'GET',
                    headers: { 'Content-Type': 'application/json' },
                }
                );
                if (!response.ok) throw new Error('Failed to fetch layer items');

                const data = await response.json();
                setLayerData(data);
            } catch (error) {
                console.error(`Error fetching layer data for layer ${layerId}:`, error);
                setLayerData(null);
            } finally {
                setLoading(false);
            }
        };
  
        fetchLayerData();
    }, [layerId]);

    // Show loading animation if the layers are currently loading
    if (loading) return <LoadingIndicator/>
  
    return (
        <div className="p-6">
            {/* Default view for layer with no items */}
            {(!items || items.length === 0) && details && (
                <div className="p-6 text-center">
                    <h2 className="text-lg font-semibold text-gray-800">
                        {details[0]?.layer_name || 'No Layer Name'}
                    </h2>
                    <p className="text-sm text-gray-600 mt-1">
                        {details[0]?.layer_description || 'No description available.'}
                    </p>
                    
                </div>
            )}

            {/* Selected Item View */}
            {item && (
                <div className="mb-6 bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden">
                    {/* Header (Name) */}
                    <div className="p-6 bg-[#21506F] rounded-t-xl flex justify-between items-center">
                        <div className="flex flex-col">
                            <p className="text-xl font-bold text-white capitalize">
                                {capitalizeWords(item?.name || '')}
                            </p>
                        </div>
                    </div>

                    {/* General Layer Item Info */}
                    <div className="px-6 pt-4 space-y-4">
                        {/* Activity Date (hide for business discovery)*/}
                        {formattedDate &&
                            <div className="flex flex-col items-start space-y-1">
                                <span className="font-semibold text-sm">Activity Date</span>
                                <span className="text-sm">{formattedDate}</span>
                            </div>
                        }

                        {/* Description */}
                        {item?.description &&
                            <div className="flex flex-col items-start space-y-1">
                                <p className="font-semibold text-sm">Description</p>
                                <div
                                    className='text-sm'
                                    dangerouslySetInnerHTML={{ __html: parseMarkdown(item?.description) }}
                                />
                            </div>
                        }

                        {/* Category */}
                        <div className="flex flex-col items-start space-y-1">
                            <span className="font-semibold text-sm">Category</span>
                            <span className="text-sm">{item?.category || 'N/A'}</span>
                        </div>

                        {/* Value */}
                        {item?.value &&
                        <div className="flex flex-col items-start space-y-1">
                            <span className="font-semibold text-sm">Value</span>
                            <span className="text-sm">{formatValueBase(item.value, 'currency') }</span>
                        </div>
                        }
                    </div>

                    {/* Table Content */}
                    <div className="p-6 space-y-4 text-gray-700">
                        <div className="overflow-x-auto">
                            <table className="min-w-full border-collapse border border-gray-300">
                                <thead>
                                    <tr className="bg-gray-100">
                                        <th className="border border-gray-300 px-4 py-2 text-left text-sm font-semibold">Field</th>
                                        <th className="border border-gray-300 px-4 py-2 text-left text-sm font-semibold">Value</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {/* Attributes */}
                                    {item?.attributes?.map((attr) => (
                                            <tr key={attr.name} className="hover:bg-gray-50">
                                                <td className="border border-gray-300 px-4 py-2 text-sm font-medium">{capitalizeWords(attr.name)}</td>
                                                <td className="border border-gray-300 px-4 py-2 text-sm" dangerouslySetInnerHTML={{ __html: parseMarkdown(attr.value) || "N/A" }}>
                                                </td>
                                            </tr>
                                        ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
  
export default MapItemDetails;